import type * as React from 'react'

import clsx from 'clsx'

import {
  Flex,
  Card,
  AudienceIcon,
  AllChannelsIcon,
  CriticalIcon,
  Text,
  Link,
  ShoppingCartIcon,
  Tooltip,
  InfoIcon,
  useMediaQuery,
} from '@buffer-mono/popcorn'

import { type FragmentOf, readFragment } from '~publish/graphql'

import {
  getChannelPricingDescription,
  getUsersCopy,
} from './BillingSettings.helpers'
import PlanCard from './PlanCard'
import {
  numberWithCurrency,
  OrganizationDataForBilling_AccountOrganization,
  PlanContent_Billing,
  UpcomingInvoice_Billing,
} from './PlanContent'

import styles from './PlanCards.module.css'

const openTieredPricingModal = (cta: string): void => {
  const { MODALS, actions } = window?.appshell || {}
  actions.openModal(MODALS.quantityUpdateTieredPricing, {
    cta,
    upgradePathName: 'account-upgrade',
  })
}

const openSubscriptionUpdate = (cta: string): void => {
  const { MODALS, actions } = window?.appshell || {}
  actions.openModal(MODALS.subscriptionUpdate, {
    cta,
    upgradePathName: 'account-upgrade',
    shouldPickModalOnOrganizationState: true,
  })
}

/**
 * Formats the upcoming invoice amount.
 * @param totalInvoiceAmount - The total invoice amount.
 * @param totalInvoiceAmountExcludingTax - The total invoice amount excluding tax.
 * @param taxAmount - The tax amount.
 * @param currency - The currency to format the amount in.
 * @returns The formatted upcoming invoice amount.
 */
const getUpcomingInvoiceCopy = (
  upcomingInvoiceData: FragmentOf<typeof UpcomingInvoice_Billing>,
): {
  upcomingInvoiceAmountExcludingTax: string
  upcomingInvoiceAmountTax: string
} => {
  const { upcomingInvoice } = readFragment(
    UpcomingInvoice_Billing,
    upcomingInvoiceData,
  )

  if (!upcomingInvoice) {
    return {
      upcomingInvoiceAmountExcludingTax: '$0.00',
      upcomingInvoiceAmountTax: '$0.00',
    }
  }

  // TODO: can we make these non-nullable on the core-api?
  const { amountWithoutTax, amountTax, currency } = upcomingInvoice

  return {
    upcomingInvoiceAmountExcludingTax: numberWithCurrency(
      Number(amountWithoutTax ?? 0),
      currency ?? 'USD',
    ),
    upcomingInvoiceAmountTax: numberWithCurrency(
      Number(amountTax ?? 0),
      currency ?? 'USD',
    ),
  }
}

interface PlanCardsProps {
  // Add props here as needed
  organizationData: FragmentOf<
    typeof OrganizationDataForBilling_AccountOrganization
  > | null
  billingData: FragmentOf<typeof PlanContent_Billing>
}

const PlanCards: React.FC<PlanCardsProps> = ({
  organizationData,
  billingData,
}) => {
  // Read fragments
  const billing = readFragment(PlanContent_Billing, billingData)
  const organization = readFragment(
    OrganizationDataForBilling_AccountOrganization,
    organizationData,
  )

  const hasPaymentDetails =
    Boolean(organization?.billing?.paymentDetails?.hasPaymentDetails) ?? false
  const subscription = billing.subscription

  // Non-agency tiered pricing has more than 2 pricing tiers
  const isOnNonAgencyTieredPricing = billing.pricingTiers.length > 2
  const isPayingWithBank = billing.paymentDetails.paymentMethod === 'bank'
  const isOnTrial = subscription.trial?.isActive ?? false

  // Copy
  const usersCopy = getUsersCopy(subscription.plan.limits.members ?? 0)
  const pricingDescription = getChannelPricingDescription({
    plan: subscription.plan,
    isOnTrial,
    isOnTieredPricing: isOnNonAgencyTieredPricing,
  })
  const { upcomingInvoiceAmountExcludingTax, upcomingInvoiceAmountTax } =
    getUpcomingInvoiceCopy(billing)

  // Channel count and limits
  const connectedChannelsCount = organization?.channels.length ?? 0
  const lockedChannelsCount =
    organization?.channels.filter((channel: { isLocked: boolean }) => {
      return channel.isLocked
    }).length ?? 0
  const channelsLimit = organization?.limits.channels

  const parseUpcomingInvoiceAmount = upcomingInvoiceAmountTax.replace(
    /[^0-9.]+/g,
    '',
  )

  const handleUnlockChannels = (): void => {
    if (isOnNonAgencyTieredPricing) {
      openTieredPricingModal('billing-plansCard-unlock-1')
    } else {
      openSubscriptionUpdate('billing-plansCard-unlock-1')
    }
  }

  const isMobile = useMediaQuery('(width < 640px)')

  const isAgencyPlan = subscription.plan.id === 'agency'

  return (
    <Card className={styles.container}>
      <Flex
        direction="row"
        align="center"
        justify="around"
        className={styles.statCardContainer}
      >
        {/* Users */}
        <PlanCard className={clsx({ [styles.agencyPlanCard]: isAgencyPlan })}>
          <PlanCard.Icon>
            <AudienceIcon />
          </PlanCard.Icon>
          <PlanCard.TextContent>
            <PlanCard.Title>Users</PlanCard.Title>
            <PlanCard.Info>{usersCopy}</PlanCard.Info>
          </PlanCard.TextContent>
          <PlanCard.Value>{organization?.members?.totalCount}</PlanCard.Value>
        </PlanCard>

        {/* Channels */}
        <PlanCard className={clsx({ [styles.agencyPlanCard]: isAgencyPlan })}>
          <PlanCard.Icon>
            <AllChannelsIcon />
          </PlanCard.Icon>
          <PlanCard.TextContent>
            <PlanCard.Title>Channels</PlanCard.Title>
            <PlanCard.Info>{pricingDescription}</PlanCard.Info>
            {/* Mobile only */}
            {/* Depending on screen size we move the layout slightly to position text correctly */}
            {isMobile && lockedChannelsCount > 0 && (
              <PlanCard.FootNote>
                <CriticalIcon color="critical" />
                <Text size="sm" color="critical">
                  {lockedChannelsCount} channel
                  {lockedChannelsCount !== 1 ? 's' : ''} locked
                </Text>
                {!isPayingWithBank && (
                  <Link as="button" onClick={handleUnlockChannels}>
                    Unlock
                  </Link>
                )}
              </PlanCard.FootNote>
            )}
          </PlanCard.TextContent>
          <PlanCard.Value>
            {isOnTrial
              ? `${connectedChannelsCount}`
              : `${connectedChannelsCount}/${channelsLimit}`}
          </PlanCard.Value>
          {/* Large screens only */}
          {/* Depending on screen size we move the layout slightly to position text correctly */}
          {lockedChannelsCount > 0 && !isMobile && (
            <PlanCard.FootNote>
              <CriticalIcon color="critical" />
              <Text size="sm" color="critical">
                {lockedChannelsCount} channel
                {lockedChannelsCount !== 1 ? 's' : ''} locked
              </Text>
              {!isPayingWithBank && (
                <Link as="button" onClick={handleUnlockChannels}>
                  Unlock
                </Link>
              )}
            </PlanCard.FootNote>
          )}
        </PlanCard>

        {/* Upcoming bill */}
        <PlanCard className={clsx({ [styles.agencyPlanCard]: isAgencyPlan })}>
          <PlanCard.Icon>
            <ShoppingCartIcon />
          </PlanCard.Icon>
          <PlanCard.TextContent>
            <PlanCard.Title>
              {' '}
              {isOnTrial ? 'First bill after trial' : 'Upcoming bill'}
            </PlanCard.Title>
            <PlanCard.Info>
              <Tooltip
                content="This amount could change if adjustments are made mid-billing cycle to your number of connected channels. We'll always account for those changes and include the details in your invoice."
                side="bottom"
              >
                <Text size="md" color="subtle">
                  Usage changes{' '}
                  <InfoIcon
                    size="small"
                    style={{ verticalAlign: 'middle', marginTop: '-2px' }}
                  />
                </Text>
              </Tooltip>
            </PlanCard.Info>
            {/* Mobile only */}
            {/* Depending on screen size we move the layout slightly to position text correctly */}
            {isMobile && !hasPaymentDetails && isOnTrial && (
              <PlanCard.FootNote>
                You won&apos;t be charged unless you choose to subscribe
              </PlanCard.FootNote>
            )}
          </PlanCard.TextContent>
          {!hasPaymentDetails && isOnTrial && !isMobile && (
            <PlanCard.FootNote>
              You won&apos;t be charged unless you choose to subscribe
            </PlanCard.FootNote>
          )}
          {!isOnTrial && (
            <div>
              <PlanCard.Value>
                {upcomingInvoiceAmountExcludingTax}
              </PlanCard.Value>
              {parseUpcomingInvoiceAmount !== '0.00' && (
                <PlanCard.FootNote>
                  Plus tax ({upcomingInvoiceAmountTax})
                </PlanCard.FootNote>
              )}
            </div>
          )}
        </PlanCard>
      </Flex>
    </Card>
  )
}

export default PlanCards
